import { graphql } from "babel-plugin-relay/macro";
import { Card } from "primereact/card";
import React from "react";
import {
	RestartIfPassedContentConfigEditor_TreeNodeFragment$key,
	RestartIfPassedContentConfigType,
} from "../../../../../../../__generated__/RestartIfPassedContentConfigEditor_TreeNodeFragment.graphql";
import { useFragment, useMutation } from "react-relay";
import {
	RestartIfPassedContentConfigEditor_SetCanRestartIfPassedPassContentConfigMutation,
	SetCanRestartIfPassedContentConfigInput,
} from "../../../../../../../__generated__/RestartIfPassedContentConfigEditor_SetCanRestartIfPassedPassContentConfigMutation.graphql";
import {
	RestartIfPassedContentConfigEditor_SetCanNotRestartIfPassedContentConfigMutation,
	SetCanNotRestartIfPassedContentConfigInput,
} from "../../../../../../../__generated__/RestartIfPassedContentConfigEditor_SetCanNotRestartIfPassedContentConfigMutation.graphql";
import { Config } from "../components/Config.interfaces";
import { ConfigItem } from "../components/ConfigItem";
import { ConfigDropdownOptions } from "../components/ConfigDropdown";

const TREE_NODE_FRAGMENT = graphql`
	fragment RestartIfPassedContentConfigEditor_TreeNodeFragment on TreeNode {
		id
		typeDefinition {
			... on ContentTypeDefinition {
				restartIfPassedConfig {
					configType
				}
			}
		}
	}
`;

const SET_CAN_RESTART_IF_PASSED_CONTENT_CONDITION_CONFIGURATION_MUTATION = graphql`
	mutation RestartIfPassedContentConfigEditor_SetCanRestartIfPassedPassContentConfigMutation(
		$input: SetCanRestartIfPassedContentConfigInput!
	) {
		Admin {
			Tree {
				setCanRestartIfPassedContentConfig(input: $input) {
					contentNode {
						...RestartIfPassedContentConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

const SET_CANNOT_RESTART_IF_PASSED_CONTENT_CONDITION_CONFIGURATION_MUTATION = graphql`
	mutation RestartIfPassedContentConfigEditor_SetCanNotRestartIfPassedContentConfigMutation(
		$input: SetCanNotRestartIfPassedContentConfigInput!
	) {
		Admin {
			Tree {
				setCanNotRestartIfPassedContentConfig(input: $input) {
					contentNode {
						...RestartIfPassedContentConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

interface Props {
	treeNodeFragmentRef: RestartIfPassedContentConfigEditor_TreeNodeFragment$key;
}

export const RestartIfPassedContentConfigEditor = ({ treeNodeFragmentRef }: Props) => {
	const treeNode = useFragment<RestartIfPassedContentConfigEditor_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const [setCanRestartIfPassedContentConfig] =
		useMutation<RestartIfPassedContentConfigEditor_SetCanRestartIfPassedPassContentConfigMutation>(
			SET_CAN_RESTART_IF_PASSED_CONTENT_CONDITION_CONFIGURATION_MUTATION,
		);
	const [setCanNotRestartIfPassedContentConfig] =
		useMutation<RestartIfPassedContentConfigEditor_SetCanNotRestartIfPassedContentConfigMutation>(
			SET_CANNOT_RESTART_IF_PASSED_CONTENT_CONDITION_CONFIGURATION_MUTATION,
		);

	const configType: RestartIfPassedContentConfigType = treeNode.typeDefinition
		.restartIfPassedConfig?.configType as RestartIfPassedContentConfigType;

	const canRestartIfPassed: Config<
		RestartIfPassedContentConfigType,
		SetCanRestartIfPassedContentConfigInput
	> = {
		configKey: "RestartIfPassedContent_Can",
		addMutation: (input: SetCanRestartIfPassedContentConfigInput) => {
			setCanRestartIfPassedContentConfig({
				variables: {
					input,
				},
			});
		},
		editable: false,
	};

	const canNotRestartIfPAssed: Config<
		RestartIfPassedContentConfigType,
		SetCanNotRestartIfPassedContentConfigInput
	> = {
		configKey: "RestartIfPassedContent_CanNot",
		addMutation: (input: SetCanNotRestartIfPassedContentConfigInput) => {
			setCanNotRestartIfPassedContentConfig({
				variables: {
					input,
				},
			});
		},
		editable: false,
	};

	const configs: Config<RestartIfPassedContentConfigType, any | never>[] = [
		canRestartIfPassed,
		canNotRestartIfPAssed,
	];

	const options: ConfigDropdownOptions<RestartIfPassedContentConfigType>[] = configs.map((c) => {
		return {
			label: c.configKey,
			value: c.configKey,
		};
	});

	return (
		<Card className="mb-2">
			<h2>Wiederholen falls bereits bestanden</h2>

			<ConfigItem<RestartIfPassedContentConfigType, string>
				isPresentational={false}
				configType={configType}
				canEdit={
					configs.find(
						(c) =>
							c.configKey ===
							treeNode.typeDefinition.restartIfPassedConfig?.configType,
					)?.editable as boolean
				}
				configOptions={options}
				onChange={(e) => {
					const selectedConfig = configs.find((c) => c.configKey === e.value);

					if (selectedConfig) {
						selectedConfig.addMutation({
							contentNodeId: treeNode.id,
							...selectedConfig.addMutationPayload,
						});
					}
				}}
			/>
		</Card>
	);
};
